"use client";

import { Mic } from "lucide-react";
import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import { Component as Loader } from "./ai-loader";

interface AIVoiceInputProps {
  onStart?: () => void;
  onStop?: (duration: number) => void;
  visualizerBars?: number;
  demoMode?: boolean;
  demoInterval?: number;
  className?: string;
  playingText?: string;
  readyText?: string;
  isPlaying?: boolean;
  loadingText?: string;
  colorScheme?: "dark" | "light";
}

export function AIVoiceInput({
  onStart,
  onStop,
  visualizerBars = 48,
  demoMode = false,
  demoInterval = 3000,
  className,
  playingText = "Playing narration...",
  readyText = "Narration ready",
  isPlaying,
  loadingText,
  colorScheme = "dark",
}: AIVoiceInputProps) {
  const [submitted, setSubmitted] = useState(false);
  const [time, setTime] = useState(0);
  const [isClient, setIsClient] = useState(false);
  const [isDemo, setIsDemo] = useState(demoMode);
  const activeLoadingText = loadingText ?? playingText;

  useEffect(() => {
    setIsClient(true);
  }, []);

  // 当 isPlaying prop 存在时，使用外部控制的状态
  useEffect(() => {
    if (isPlaying !== undefined) {
      setSubmitted(isPlaying);
    }
  }, [isPlaying]);

  useEffect(() => {
    let intervalId: NodeJS.Timeout;

    if (submitted) {
      onStart?.();
      intervalId = setInterval(() => {
        setTime((t) => t + 1);
      }, 1000);
    } else {
      onStop?.(time);
      // 只在非外部控制模式下重置时间
      if (isPlaying === undefined) {
        setTime(0);
      }
    }

    return () => clearInterval(intervalId);
  }, [submitted, time, onStart, onStop, isPlaying]);

  useEffect(() => {
    if (!isDemo) return;

    let timeoutId: NodeJS.Timeout;
    const runAnimation = () => {
      setSubmitted(true);
      timeoutId = setTimeout(() => {
        setSubmitted(false);
        timeoutId = setTimeout(runAnimation, 1000);
      }, demoInterval);
    };

    const initialTimeout = setTimeout(runAnimation, 100);
    return () => {
      clearTimeout(timeoutId);
      clearTimeout(initialTimeout);
    };
  }, [isDemo, demoInterval]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const handleClick = () => {
    if (isDemo) {
      setIsDemo(false);
      setSubmitted(false);
    } else {
      setSubmitted((prev) => !prev);
    }
  };

  const isLightScheme = colorScheme === "light";

  return (
    <div className={cn("w-full", className)}>
      <div className="relative max-w-xl w-full mx-auto flex items-center flex-col gap-2">
      <Loader overlay={false} size={72} text="Explaining..." className="shrink-0" />
        {/* <button
          className={cn(
            "group rounded-2xl flex items-center justify-center transition-all duration-300",
            submitted
              ? "sm:w-20 w-16 sm:h-20 h-16 bg-black/10 dark:bg-white/10"
              : "sm:w-16 w-12 sm:h-16 h-12 bg-none hover:bg-black/10 dark:hover:bg-white/10"
          )}
          type="button"
          onClick={handleClick}
        >
          {submitted ? (
            <Loader overlay={false} size={56} text="" className="shrink-0" />
          ) : (
            <Mic className="w-6 h-6 text-black/70 dark:text-white/70" />
          )}
        </button> */}

        <span
          className={cn(
            "font-mono text-sm transition-opacity duration-300",
            submitted
              ? isLightScheme
                ? "text-white/80"
                : "text-black/70 dark:text-white/70"
              : isLightScheme
                ? "text-white/50"
                : "text-black/30 dark:text-white/30"
          )}
        >
          {formatTime(time)}
        </span>

        <div className="h-4 w-64 flex items-center justify-center gap-0.5">
          {[...Array(visualizerBars)].map((_, i) => (
            <div
              key={i}
              className={cn(
                "w-0.5 rounded-full transition-all duration-300",
                submitted
                  ? isLightScheme
                    ? "bg-white/70 animate-pulse"
                    : "bg-black/50 dark:bg-white/50 animate-pulse"
                  : isLightScheme
                    ? "bg-white/20 h-1"
                    : "bg-black/10 dark:bg-white/10 h-1"
              )}
              style={
                submitted && isClient
                  ? {
                      height: `${20 + Math.random() * 80}%`,
                      animationDelay: `${i * 0.05}s`,
                    }
                  : undefined
              }
            />
          ))}
        </div>

        {/* <p
          className={cn(
            "h-4 text-xs font-medium uppercase tracking-[0.2em] transition-colors",
            submitted
              ? "text-black/70 dark:text-white/70"
              : "text-black/30 dark:text-white/30"
          )}
          aria-live="polite"
        >
          {submitted ? activeLoadingText : readyText}
        </p> */}
      </div>
    </div>
  );
}
